import { type WritableDraft } from 'immer';
import { type Observable, of } from 'rxjs';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  applyFieldSize,
  getElement,
  getInitialProps,
  patchFieldLabel,
  toOptionPickerDefaultValue,
  toSelectionLayout,
  toTileSelectorMode,
  upsertElement,
} from '../../../ai';

import { type CheckboxContext } from './checkbox.definition';

export class CheckboxElementHandler extends AbstractAiElementHandler<'checkbox'> {
  readonly type = 'checkbox';

  prepare(context: CheckboxContext): Observable<CheckboxContext> {
    this.resolveElementIcon(context.element);
    return of(context);
  }

  process(context: CheckboxContext, draft: WritableDraft<EngineState>): void {
    let props: PartialPropsOf<'checkbox'> = getInitialProps<'checkbox'>(
      draft,
      context.element.id,
      {},
    );
    props = patchFieldLabel<'checkbox'>(props, context.element);

    if (context.element.isReadOnly !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: { checkbox: { readonly: context.element.isReadOnly } },
      });
    }

    if (context.element.defaultValue !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: {
          data: { defaultValue: toOptionPickerDefaultValue(context.element.defaultValue) },
        },
      });
    }

    if (context.element.template !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: { mode: toTileSelectorMode(context.element.template) },
      });
    }

    if (context.element.layout !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: { checkbox: { layout: toSelectionLayout(context.element.layout) } },
      });
    }

    if (context.element.maxColumns !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: {
          checkbox: { maxColumns: { mode: 'value', value: context.element.maxColumns } },
        },
      });
    }

    if (context.element.minColumnWidth !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: {
          checkbox: { minWidth: { mode: 'value', value: context.element.minColumnWidth } },
        },
      });
    }

    if (context.element.displayProperties?.sortBy !== undefined) {
      props = applyPropsPatch<'checkbox'>(props, {
        content: { sort: { field: context.element.displayProperties.sortBy, direction: 'asc' } },
      });
    }

    const { elementId } = upsertElement(draft, context, props);
    applyFieldSize<'checkbox'>(getElement(draft, elementId, 'checkbox'), context.element);
  }
}
